import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import {
  stcMovementTypeService,
  StcMovementTypeDto,
  BulkUpdateStcMovementTypeRequest
} from '../../../../../services/stcMovementTypeService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { MovementTypeSidePanel } from './MovementTypeSidePanel';
import { MovementTypeCreateModal } from './MovementTypeCreateModal';

interface MovementTypeDataTableProps {
  refreshTrigger?: number;
}

export const MovementTypeDataTable: React.FC<MovementTypeDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [data, setData] = useState<StcMovementTypeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing - uses bulkUpdate to support changing all fields including key
  const inlineEditConfig = useInlineEdit<StcMovementTypeDto>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, editedData) => {
      const request: BulkUpdateStcMovementTypeRequest = {
        companyNo: originalRow.companyNo,
        movementTypeNo: (editedData.movementTypeNo as number) ?? originalRow.movementTypeNo,
        description1: (editedData.description1 as string) ?? originalRow.description1
      };
      await stcMovementTypeService.bulkUpdate([request]);
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminMovement.errors.saveFailed', {}, 'Feil ved lagring'))
  });

  // Load data
  useEffect(() => {
    loadData();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const movementTypes = await stcMovementTypeService.getCurrent().catch(() =>
        stcMovementTypeService.getAll().catch(() => [])
      );
      setData(movementTypes);
    } catch (err: any) {
      console.error('Error loading movement type data:', err);
      setError(t('manager.modules.adminMovement.movementType.loadError', {}, 'Kunne ikke laste bevegelseskoder. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  // Column factory
  const columnFactory = createColumnFactory<StcMovementTypeDto>();

  // Column definitions - all fields editable
  const columns: ColumnDefinition<StcMovementTypeDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', t('manager.modules.adminMovement.movementType.fields.movementTypeNo', {}, 'Bevegelseskode nr.'), {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('description1', 'description1', t('manager.modules.adminMovement.fields.description', {}, 'Beskrivelse'), {
      minWidth: 250,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [t]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminMovement.movementType.selectAtLeastOne', {}, 'Velg minst én bevegelseskode for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  // Table actions
  const tableActions: TableAction<StcMovementTypeDto>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminMovement.movementType.createTitle', {}, 'Ny bevegelseskode'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminMovement.maintenance', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  // Build selected rows map for SidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'movement-type-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <MovementTypeSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <MovementTypeCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
